const PERCENT_OFF_REGEX = /(\d+(?:\.\d+)?)\s*%\s*off\b/i;
const ORIGINAL_PRICE_WAS_REGEX = /original\s+price\s+was/i;
/**
 * Matches "2 FOR $69", "3FOR$99", OCR "3ForR$95", "2 for 69"
 * Allows junk letters between FOR and $ (e.g. ForR).
 */
const FOR_OFFER_REGEX =
  /(\d+)\s*(?:for|tor)[a-z]*\s*\$?\s*(\d+(?:\.\d+)?)/gi;

export const parsePercentOff = (text) => {
  if (!text) return null;
  const match = String(text).trim().match(PERCENT_OFF_REGEX);
  return match ? parseFloat(match[1]) : null;
};

export const isPercentOffPromo = (promo) => parsePercentOff(promo?.text) != null;

/** Legacy scrape shape only — new scrapes store SINGLE PROMO with sale on promo.price */
export const isOriginalPriceWasPromo = (promo) =>
  ORIGINAL_PRICE_WAS_REGEX.test(String(promo?.text || "").trim());

const parseUsd = (value) => {
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
};

/** Normalize OCR junk before splitting multi-buy labels. */
export const normalizeMultiForPromoText = (text) =>
  String(text || "")
    .replace(/FOorR/gi, "FOR")
    .replace(/ForR/gi, "FOR")
    .replace(/\[\s*\|/g, "|")
    .trim();

/**
 * Pull every "N FOR $X" offer out of a glued multi-buy label.
 * Handles: "2 FOR $69 / 3 FOR $99", "2FOR $25 3 FOR $35", "3ForR$95|2 FOR $69"
 */
export const parseForOffersFromText = (text) => {
  const raw = normalizeMultiForPromoText(text);
  if (!raw) return [];
  const offers = [];
  const re = new RegExp(FOR_OFFER_REGEX.source, "gi");
  let match;
  while ((match = re.exec(raw)) !== null) {
    const quantity = parseInt(match[1], 10);
    const bundleLocal = parseFloat(match[2]);
    if (!quantity || !(bundleLocal > 0)) continue;
    const amount =
      Number.isInteger(bundleLocal) || bundleLocal % 1 === 0
        ? String(Math.round(bundleLocal))
        : String(bundleLocal);
    offers.push({
      quantity,
      bundleLocal,
      label: `${quantity} FOR $${amount}`,
    });
  }
  return offers;
};

/** Join multi-buy offers with " / " — e.g. "2 FOR $25 / 3 FOR $35". */
export const formatMultiForPromoLabel = (text) => {
  const offers = parseForOffersFromText(text);
  if (offers.length < 2) return null;
  return offers.map((o) => o.label).join(" / ");
};

/**
 * True when this promo text has two+ "N FOR $X" offers glued together
 * (slash, pipe, bracket, or just spaced — e.g. "2FOR $25 3 FOR $35").
 */
export const isGluedMultiForPromo = (text) =>
  parseForOffersFromText(text).length >= 2;

export const getPromoDisplayText = (promo) => {
  if (isOriginalPriceWasPromo(promo)) return "SINGLE PROMO";
  return promo?.text || "";
};

/**
 * Expand glued multi-buy into one row per offer (with unit USD).
 * Handles: "2FOR $25 3 FOR $35", "2 FOR $25 / 3 FOR $35", "2FOR$25/3FOR$35"
 */
export const expandPromosForDisplay = (promos, getUsdPerLocal, nativeCurrency) => {
  if (!Array.isArray(promos) || promos.length === 0) return [];

  const expanded = [];
  for (const promo of promos) {
    const rawText = promo?.text || "";

    if (!isGluedMultiForPromo(rawText)) {
      expanded.push(promo);
      continue;
    }

    const offers = parseForOffersFromText(rawText);
    const usdPerLocal =
      typeof getUsdPerLocal === "function"
        ? getUsdPerLocal(nativeCurrency)
        : null;

    for (const offer of offers) {
      const unitLocal = offer.bundleLocal / offer.quantity;
      let unitUsd = null;
      if (usdPerLocal != null && usdPerLocal > 0 && !Number.isNaN(usdPerLocal)) {
        unitUsd = unitLocal * usdPerLocal;
      }

      expanded.push({
        ...promo,
        text: offer.label,
        price: unitUsd != null ? unitUsd : promo.price,
        _expandedFromMultiFor: true,
      });
    }
  }
  return expanded;
};

export const getOriginalWasShelfUsd = (promo) => {
  if (!isOriginalPriceWasPromo(promo)) return null;
  if (promo?.price == null || promo.price === "") return null;
  return parseUsd(promo.price);
};

export const getPromoPriceUsd = (promo, defaultPriceUsd) => {
  // Legacy ORIGINAL PRICE WAS: promo.price is "was", sale is latest_price.
  if (isOriginalPriceWasPromo(promo)) {
    return parseUsd(defaultPriceUsd);
  }

  if (promo?.price != null && promo.price !== "") {
    const storedPrice = parseUsd(promo.price);
    if (storedPrice != null) return storedPrice;
  }

  const percent = parsePercentOff(promo?.text);
  const defaultPrice = parseUsd(defaultPriceUsd);

  if (
    percent != null &&
    defaultPrice != null &&
    defaultPrice > 0 &&
    percent > 0 &&
    percent < 100
  ) {
    return defaultPrice * (1 - percent / 100);
  }

  return null;
};

export const promosHaveDisplayablePrices = (promos, defaultPriceUsd) =>
  Array.isArray(promos) &&
  promos.some((promo) => getPromoPriceUsd(promo, defaultPriceUsd) != null);

/**
 * True shelf / default for display.
 * - Legacy ORIGINAL PRICE WAS: promo.price is the "was"/shelf (checked first;
 *   those rows also have a stored price, so they must not fall into the sale path)
 * - New shape (incl. % Off with sale on promo.price): latest_price is shelf → return it
 * - Text-only % Off / no priced promo: latest_price is still the shelf default
 */
export const getEffectiveDefaultPriceUsd = (promos, latestPriceUsd) => {
  const originalWasPrices = (promos || [])
    .map(getOriginalWasShelfUsd)
    .filter((price) => price != null);

  if (originalWasPrices.length > 0) {
    return Math.max(...originalWasPrices);
  }

  // Priced sale promos (SINGLE PROMO, "7% Off" + price, etc.) and unpriced
  // badges: DEFAULT is always the product shelf (latest_price).
  return latestPriceUsd;
};
